
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import en from '@/i18n/en';
import es from '@/i18n/es';

// Define types for the Language Context
interface LanguageContextProps {
  language: SupportedLanguages;
  setLanguage: (lang: SupportedLanguages) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

export type SupportedLanguages = 'en' | 'es';

const translations = {
  en,
  es,
};

const LanguageContext = createContext<LanguageContextProps>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

export const useLanguage = () => {
  return useContext(LanguageContext);
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getInitialLanguage = (): SupportedLanguages => {
    try {
      const savedLanguage = window.localStorage?.getItem('planwise-language');
      return (savedLanguage === 'en' || savedLanguage === 'es') ? savedLanguage : 'en';
    } catch {
      return 'en';
    }
  };

  const [language, setLanguage] = useState<SupportedLanguages>(getInitialLanguage);

  // Save language to localStorage whenever it changes
  useEffect(() => {
    try {
      window.localStorage?.setItem('planwise-language', language);
    } catch {
      // ignore storage errors (private mode, disabled storage, etc.)
    }
  }, [language]);

  // Allow translation with parameters and nested keys
  const t = (key: string, params?: Record<string, string | number>): string => {
    const currentTranslations = translations[language] || {};
    
    // First, try to find the key as a flattened key (for keys processed with flattenWithPrefix)
    if (key in currentTranslations && typeof currentTranslations[key] === 'string') {
      let translatedText = currentTranslations[key];
      
      // Replace parameters if provided
      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          translatedText = translatedText.replace(`{${paramKey}}`, String(paramValue));
        });
      }
      
      return translatedText;
    }
    
    // If not found as flattened key, support nested keys using dot notation (e.g., 'landing.newsletter.comingSoon')
    const keys = key.split('.');
    let translatedText: any = currentTranslations;
    
    for (const k of keys) {
      if (translatedText && typeof translatedText === 'object' && k in translatedText) {
        translatedText = translatedText[k];
      } else {
        // If key not found, return the original key
        translatedText = key;
        break;
      }
    }
    
    // Ensure we have a string
    if (typeof translatedText !== 'string') {
      translatedText = key;
    }
    
    // Replace parameters if provided
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translatedText = translatedText.replace(`{${paramKey}}`, String(paramValue));
      });
    }
    
    return translatedText;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
